import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { ClaimPropertyModal } from '@/components/claim/ClaimPropertyModal'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Spinner } from '@/components/ui/spinner'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { useAuth } from '@/hooks/use-auth'
import { splitAddressDisplay } from '@/lib/address'
import { db } from '@/lib/firebase'
import { parseProperty, type Property } from '@/types/models'

export function PropertiesPage() {
  return (
    <RequireAuth>
      <OwnedProperties />
    </RequireAuth>
  )
}

function OwnedProperties() {
  const { user } = useAuth()
  const [properties, setProperties] = useState<Property[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [claimOpen, setClaimOpen] = useState(false)

  const reload = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      const ownedSnap = await getDocs(collection(db, 'users', user.uid, 'propertiesOwned'))
      const ids = ownedSnap.docs
        .map((d) => {
          const data = d.data() as Record<string, unknown>
          return typeof data.propertyID === 'string' ? data.propertyID : null
        })
        .filter((id): id is string => Boolean(id))

      const loaded: Property[] = []
      await Promise.all(
        ids.map(async (id) => {
          const snap = await getDoc(doc(db, 'properties', id))
          if (snap.exists()) {
            loaded.push(parseProperty(snap.id, snap.data() as Record<string, unknown>))
          }
        }),
      )
      loaded.sort((a, b) => a.fullAddress.localeCompare(b.fullAddress))
      setProperties(loaded)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load properties')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    void reload()
  }, [reload])

  function closeClaim() {
    setClaimOpen(false)
    void reload()
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy">Your properties</h1>
          <p className="mt-1 text-sm text-muted">Homes you’ve claimed as an owner.</p>
        </div>
        <button
          type="button"
          onClick={() => setClaimOpen(true)}
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-biddle-blue text-biddle-blue transition hover:bg-biddle-blue/10"
          aria-label="Add your property"
          title="Add your property"
        >
          <Plus className="h-7 w-7" strokeWidth={2.5} aria-hidden />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <p className="text-sm text-danger">{error}</p>
      ) : properties.length === 0 ? (
        <EmptyState
          title="No claimed properties"
          description="Claim ownership, or enter a code from a Biddle letter."
          actionLabel="Add your property"
          onAction={() => setClaimOpen(true)}
        />
      ) : (
        <ul className="space-y-3">
          {properties.map((property) => {
            const { street, locality } = splitAddressDisplay(property.fullAddress)
            return (
              <li key={property.id}>
                <Link to={`/properties/${property.id}/biddles`} className="block w-full text-left">
                  <Card className="transition hover:border-biddle-blue">
                    <CardContent className="flex items-center justify-between gap-3 pt-5">
                      <div className="min-w-0">
                        <p className="font-semibold text-navy">{street || 'Address unavailable'}</p>
                        {locality ? (
                          <p className="mt-1 text-sm text-muted">{locality}</p>
                        ) : null}
                      </div>
                      <span className="shrink-0 text-sm font-semibold text-biddle-blue">
                        View Biddles
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      <ClaimPropertyModal open={claimOpen} onClose={closeClaim} />
    </div>
  )
}
